import { useEffect, useRef, useState, type DragEvent } from 'react';
import {
  createLayout,
  extractLayoutsFromPage,
  importLayout,
  uploadLayoutSourceFile,
  type AdminFamily,
  type ExtractedLayout,
} from '../../lib/adminApi';
import { formatCents } from '../../lib/format';
import { cls, FileDropzone } from './shared';

/**
 * The "+ New Layout" panel. Two steps behind one entry point:
 *
 *   1. capture — optionally paste (Ctrl+V) or drop a catalog page, which Claude reads
 *      server-side into pre-filled values. Skippable.
 *   2. review  — the layout fields plus an editable BOM table. Nothing is written until
 *      Create, so a misread page costs nothing.
 *
 * Warnings from extraction are shown inline but never disable Create — the admin is the
 * authority on what the page says.
 */

type Step = 'capture' | 'review';

/** A BOM row being edited. Prices are held as display strings so a half-typed value survives. */
interface BomRow {
  sku: string;
  description: string;
  quantity: string;
  unitPrice: string;
  warnings: string[];
}

export function NewLayoutPanel({
  families,
  extractionAvailable,
  onCreated,
  onCancel,
}: {
  families: AdminFamily[];
  extractionAvailable: boolean;
  onCreated: () => void;
  onCancel: () => void;
}) {
  // Skip straight to manual entry when the server can't extract.
  const [step, setStep] = useState<Step>(extractionAvailable ? 'capture' : 'review');

  const [familyId, setFamilyId] = useState<number | ''>(families[0]?.id ?? '');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [width, setWidth] = useState('');
  const [depth, setDepth] = useState('');
  const [rows, setRows] = useState<BomRow[]>([]);

  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [printedTotalCents, setPrintedTotalCents] = useState<number | null>(null);

  // Remaining candidates from a multi-page PDF, reviewed one at a time.
  const [queue, setQueue] = useState<ExtractedLayout[]>([]);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [reviewTotal, setReviewTotal] = useState(0);

  // Source files staged during creation; uploaded right after the layout is created.
  const [pendingPdf, setPendingPdf] = useState<File | null>(null);
  const [pendingIso, setPendingIso] = useState<File | null>(null);

  const [pageUrl, setPageUrl] = useState<string | null>(null);
  const [pageExpanded, setPageExpanded] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Object URLs are not garbage collected on their own.
  useEffect(() => () => { if (pageUrl) URL.revokeObjectURL(pageUrl); }, [pageUrl]);

  // Ctrl+V a screenshot. Only bound during the capture step so pasting into the review
  // form's own inputs behaves normally.
  useEffect(() => {
    if (step !== 'capture') return;
    const onPaste = (e: ClipboardEvent) => {
      const item = Array.from(e.clipboardData?.items ?? []).find(
        (i) => i.kind === 'file' && i.type.startsWith('image/'),
      );
      const file = item?.getAsFile();
      if (file) {
        e.preventDefault();
        void runExtract(file);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // runExtract is stable enough for this — it only reads setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  function applyCandidate(c: ExtractedLayout) {
    setFamilyId(c.familyId ?? '');
    setCode(c.code);
    setName('');
    setWidth(c.widthInches?.toString() ?? '');
    setDepth(c.depthInches?.toString() ?? '');
    setRows(
      c.items.map((i) => ({
        sku: i.sku,
        description: i.description,
        quantity: i.quantity?.toString() ?? '',
        unitPrice: i.unitPriceCents != null ? (i.unitPriceCents / 100).toFixed(2) : '',
        warnings: i.warnings,
      })),
    );
    setWarnings(c.warnings);
    setPrintedTotalCents(c.printedTotalCents);
  }

  async function runExtract(file: File) {
    setError(null);
    setExtracting(true);
    try {
      const { candidates, warnings: pageWarnings } = await extractLayoutsFromPage(file);
      if (pageUrl) URL.revokeObjectURL(pageUrl);
      // PDFs have no useful inline preview; only show the source panel for images.
      setPageUrl(file.type.startsWith('image/') ? URL.createObjectURL(file) : null);
      applyCandidate(candidates[0]!);
      setQueue(candidates.slice(1));
      setReviewIndex(0);
      setReviewTotal(candidates.length);
      if (pageWarnings.length) setWarnings((w) => [...w, ...pageWarnings]);
      setStep('review');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not read that page.');
    } finally {
      setExtracting(false);
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void runExtract(file);
  }

  function skipToManual() {
    setStep('review');
    setRows([]);
    setWarnings([]);
    setPrintedTotalCents(null);
    setReviewTotal(0);
  }

  // A row needs a SKU and a quantity of at least 1 to become a BOM line. Rows that
  // don't (an unreadable qty the admin hasn't filled in) are skipped on save — so
  // count them and say so, rather than dropping them silently.
  const usableRows = rows.filter((r) => r.sku.trim() && parseInt(r.quantity, 10) >= 1);
  const skippedRows = rows.length - usableRows.length;

  const computedTotalCents = usableRows.reduce((sum, r) => {
    const qty = parseInt(r.quantity, 10);
    const price = Math.round(parseFloat(r.unitPrice) * 100);
    return Number.isFinite(qty) && Number.isFinite(price) ? sum + qty * price : sum;
  }, 0);

  const totalsDisagree =
    printedTotalCents != null &&
    computedTotalCents > 0 &&
    Math.abs(printedTotalCents - computedTotalCents) > 1000;

  async function handleSave() {
    if (!code.trim() || familyId === '') return;
    setSaving(true);
    setError(null);
    try {
      const items = usableRows.map((r) => {
        const cents = Math.round(parseFloat(r.unitPrice) * 100);
        return {
          sku: r.sku.trim(),
          description: r.description.trim() || undefined,
          quantity: parseInt(r.quantity, 10),
          // Omitted rather than zeroed, so an existing product keeps its current price.
          unit_price_cents: Number.isFinite(cents) && cents >= 0 ? cents : undefined,
        };
      });

      const common = {
        family_id: familyId as number,
        code: code.trim(),
        name: name.trim() || undefined,
        width_inches: width ? parseFloat(width) : undefined,
        depth_inches: depth ? parseFloat(depth) : undefined,
      };

      // No BOM rows means this is the plain manual path — same call as before this feature.
      const created =
        items.length === 0 ? await createLayout(common) : await importLayout({ ...common, items });
      const newId = created.layout.id;

      // Attach any staged source files. Non-fatal: the layout already exists, so a
      // failed upload shouldn't undo the create — surface it and let the admin retry
      // from the layout's Source files tab.
      const uploadErrors: string[] = [];
      if (pendingPdf) {
        try { await uploadLayoutSourceFile(newId, 'dimensional-pdf', pendingPdf); }
        catch (e) { uploadErrors.push(`dimensional PDF — ${e instanceof Error ? e.message : 'upload failed'}`); }
      }
      if (pendingIso) {
        try { await uploadLayoutSourceFile(newId, 'isometric', pendingIso); }
        catch (e) { uploadErrors.push(`isometric — ${e instanceof Error ? e.message : 'upload failed'}`); }
      }

      onCreated();

      if (uploadErrors.length) {
        alert(
          `Layout ${created.layout.code} was created, but a source file didn't upload:\n` +
            `${uploadErrors.join('\n')}\n\nYou can add it from the layout's Source files tab.`,
        );
      }

      if (queue.length > 0) {
        const [next, ...rest] = queue;
        applyCandidate(next!);
        // A different layout — don't carry the previous one's staged source files.
        setPendingPdf(null);
        setPendingIso(null);
        setQueue(rest);
        setReviewIndex((i) => i + 1);
        setError(null);
      } else {
        onCancel();
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error creating layout');
    } finally {
      setSaving(false);
    }
  }

  const canSave = !!code.trim() && familyId !== '' && !saving;

  // Shown on BOTH steps so all three drop targets (catalog page + the two source
  // files) are visible up front; the staged files upload when the layout is created.
  const sourceFilesSection = (
    <div className="mt-4">
      <label className="text-xs text-vari-gray block mb-1">Source files (optional)</label>
      <div className="grid gap-3 sm:grid-cols-2">
        <SourceDrop
          label="2D dimensional PDF"
          accept="application/pdf,.pdf"
          validate={(f) => /\.pdf$/i.test(f.name) || f.type === 'application/pdf'}
          typeError="Please choose a PDF file."
          idle="Drop the dimensional PDF"
          file={pendingPdf}
          onFile={setPendingPdf}
        />
        <SourceDrop
          label="Hi-res isometric (PNG/JPG)"
          accept="image/png,image/jpeg,.png,.jpg,.jpeg"
          validate={(f) => /\.(png|jpe?g)$/i.test(f.name) || f.type === 'image/png' || f.type === 'image/jpeg'}
          typeError="Please choose a PNG or JPG image."
          idle="Drop the isometric image"
          file={pendingIso}
          onFile={setPendingIso}
        />
      </div>
      <p className="text-xs text-vari-gray mt-1">
        Uploaded when you create the layout. You can also add or change these later from the layout's Source files tab.
      </p>
    </div>
  );

  return (
    <div className={cls.formPanel}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-vari-navy">
          New Layout
          {reviewTotal > 1 && (
            <span className="ml-2 text-xs font-normal text-vari-gray">
              from pasted page — {reviewIndex + 1} of {reviewTotal}
            </span>
          )}
        </p>
        <button onClick={onCancel} className={cls.btnGhost}>Cancel</button>
      </div>

      {error && (
        <p className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      {step === 'capture' ? (
        <>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
              dragOver ? 'border-vari-navy bg-vari-beige/40' : 'border-vari-soft-gray hover:border-vari-navy/60 bg-gray-50'
            } ${extracting ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,application/pdf,.pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void runExtract(file);
                e.target.value = '';
              }}
            />
            <p className="text-sm text-vari-navy font-medium">
              {extracting ? 'Reading the page…' : 'Paste a catalog page screenshot (Ctrl+V)'}
            </p>
            <p className="text-xs text-vari-gray mt-1">
              or drop a PDF / image here, or click to browse
            </p>
          </div>
          <p className="text-xs text-vari-gray mt-3">
            The page is read into an editable form — nothing is saved until you review it.{' '}
            <button onClick={skipToManual} className="text-vari-blue-accessible hover:underline">
              Skip and enter manually
            </button>
          </p>
          {sourceFilesSection}
        </>
      ) : (
        <>
          {pageUrl && (
            <div className="mb-3 border border-vari-soft-gray rounded-lg overflow-hidden">
              <button
                onClick={() => setPageExpanded((v) => !v)}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-vari-navy bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <span className={`transition-transform ${pageExpanded ? 'rotate-90' : ''}`}>›</span>
                Source page
                <span className="text-vari-gray ml-auto">
                  {pageExpanded ? 'Hide' : 'Show'}
                </span>
              </button>
              {pageExpanded && (
                <img src={pageUrl} alt="Pasted catalog page" className="w-full max-h-[32rem] object-contain bg-white" />
              )}
            </div>
          )}

          {warnings.length > 0 && (
            <ul className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-0.5">
              {warnings.map((w, i) => <li key={i}>• {w}</li>)}
            </ul>
          )}

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 mb-4">
            <div>
              <label className="text-xs text-vari-gray block mb-1">Family <span className="text-red-500">*</span></label>
              <select
                className={cls.select + ' w-full'}
                value={familyId}
                onChange={(e) => setFamilyId(e.target.value ? parseInt(e.target.value, 10) : '')}
              >
                <option value="">Select…</option>
                {families.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Code <span className="text-red-500">*</span></label>
              <input className={cls.input} placeholder="e.g. 1.13" value={code} onChange={(e) => setCode(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Name</label>
              <input className={cls.input} placeholder="Optional display name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Width (in)</label>
              <input type="number" min="0" step="0.5" className={cls.input} placeholder="e.g. 144" value={width} onChange={(e) => setWidth(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Depth (in)</label>
              <input type="number" min="0" step="0.5" className={cls.input} placeholder="e.g. 96" value={depth} onChange={(e) => setDepth(e.target.value)} />
            </div>
          </div>

          <BomEditor rows={rows} setRows={setRows} />

          {sourceFilesSection}

          <div className="flex flex-wrap items-center gap-4 mt-3 text-xs">
            <span className="text-vari-navy font-medium">
              BOM total {formatCents(computedTotalCents)}
            </span>
            {skippedRows > 0 && (
              <span className="text-amber-700">
                {skippedRows} row{skippedRows === 1 ? '' : 's'} will be skipped — needs a SKU and a quantity
              </span>
            )}
            {printedTotalCents != null && (
              <span className={totalsDisagree ? 'text-amber-700' : 'text-vari-gray'}>
                printed on page {formatCents(printedTotalCents)}
                {totalsDisagree &&
                  ` — off by ${formatCents(Math.abs(printedTotalCents - computedTotalCents))}, check the rows`}
              </span>
            )}
          </div>

          <div className="flex gap-2 mt-4">
            <button onClick={() => void handleSave()} disabled={!canSave} className={cls.btnSmPrimary}>
              {saving ? 'Saving…' : queue.length > 0 ? 'Create & next' : 'Create'}
            </button>
            {extractionAvailable && reviewTotal === 0 && (
              <button onClick={() => setStep('capture')} className={cls.btnGhost}>
                ← Paste a page instead
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/** Stages one source file (dimensional PDF / isometric PNG) for upload after the
 *  layout is created. Shows the picked filename with a clear button. */
function SourceDrop({
  label,
  accept,
  validate,
  typeError,
  idle,
  file,
  onFile,
}: {
  label: string;
  accept: string;
  validate: (f: File) => boolean;
  typeError: string;
  idle: string;
  file: File | null;
  onFile: (f: File | null) => void;
}) {
  return (
    <div>
      <label className="text-[11px] text-vari-gray block mb-1">{label}</label>
      {file ? (
        <div className="flex items-center gap-2 rounded-lg border border-vari-soft-gray bg-white px-3 py-2 text-xs">
          <span className="truncate text-vari-navy font-medium flex-1" title={file.name}>{file.name}</span>
          <button
            type="button"
            onClick={() => onFile(null)}
            className="text-red-400 hover:text-red-600 transition-colors px-1"
            title="Remove"
          >
            ×
          </button>
        </div>
      ) : (
        <FileDropzone
          accept={accept}
          busy={false}
          available={false}
          idleLabel={idle}
          replaceLabel={idle}
          onFile={(f) => { if (!validate(f)) { alert(typeError); return; } onFile(f); }}
        />
      )}
    </div>
  );
}

/** Editable BOM rows. SKUs not yet in the catalog are created on save. */
function BomEditor({
  rows,
  setRows,
}: {
  rows: BomRow[];
  setRows: React.Dispatch<React.SetStateAction<BomRow[]>>;
}) {
  function update(i: number, patch: Partial<BomRow>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs text-vari-gray">Bill of materials ({rows.length})</label>
        <button
          onClick={() =>
            setRows((prev) => [...prev, { sku: '', description: '', quantity: '1', unitPrice: '', warnings: [] }])
          }
          className="text-xs text-vari-blue-accessible hover:underline"
        >
          + add row
        </button>
      </div>

      <div className={cls.tableWrap}>
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className="text-left px-3 py-2 font-semibold text-vari-gray uppercase tracking-wide w-28">SKU</th>
              <th className="text-left px-3 py-2 font-semibold text-vari-gray uppercase tracking-wide">Description</th>
              <th className="text-left px-3 py-2 font-semibold text-vari-gray uppercase tracking-wide w-20">Qty</th>
              <th className="text-left px-3 py-2 font-semibold text-vari-gray uppercase tracking-wide w-28">Unit $</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-vari-gray">
                  No line items. Add rows here, or leave empty to create the layout on its own.
                </td>
              </tr>
            ) : (
              rows.map((r, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="px-2 py-1">
                    <input className={cls.input} value={r.sku} onChange={(e) => update(i, { sku: e.target.value })} />
                  </td>
                  <td className="px-2 py-1">
                    <input className={cls.input} value={r.description} onChange={(e) => update(i, { description: e.target.value })} />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className={`${cls.input} ${!r.quantity ? 'border-amber-400 bg-amber-50' : ''}`}
                      value={r.quantity}
                      onChange={(e) => update(i, { quantity: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`${cls.input} ${!r.unitPrice ? 'border-amber-400 bg-amber-50' : ''}`}
                      value={r.unitPrice}
                      onChange={(e) => update(i, { unitPrice: e.target.value })}
                    />
                  </td>
                  <td className="px-1 py-1 text-center">
                    <button
                      onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
                      title={r.warnings.length ? r.warnings.join('; ') : 'Remove row'}
                      className="text-red-400 hover:text-red-600 transition-colors px-1"
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {rows.some((r) => r.warnings.length > 0) && (
        <ul className="mt-1 text-xs text-amber-800 space-y-0.5">
          {rows.flatMap((r, i) =>
            r.warnings.map((w, j) => (
              <li key={`${i}-${j}`}>• Row {i + 1} ({r.sku || 'no SKU'}): {w}</li>
            )),
          )}
        </ul>
      )}
    </div>
  );
}
